import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/common/Navbar";
import Footer from "../components/common/Footer";
import Notification from "../components/common/Notification";
// --- NEW: Import FaEdit icon ---
import { FaTrash, FaPlus, FaEdit, FaTimes } from "react-icons/fa";
import styles from "./AdminPage.module.css";

function AdminCategoriesPage() {
  const [categories, setCategories] = useState([]);

  // Form State
  const [name, setName] = useState("");
  const [type, setType] = useState("Product");
  const [menuSection, setMenuSection] = useState("Drink");
  const [imageFile, setImageFile] = useState(null);

  // Edit Mode State
  const [editingId, setEditingId] = useState(null); // If null, we are adding new
  const [existingImageUrl, setExistingImageUrl] = useState("");

  const [isLoading, setIsLoading] = useState(true);
  const [notification, setNotification] = useState(null);

  const showToast = (message, type = "success") => {
    setNotification({ message, type });
  };

  const fetchCategories = async () => {
    try {
      setIsLoading(true);
      const response = await fetch("/api/categories");
      if (!response.ok) throw new Error("Could not fetch categories.");
      const data = await response.json();
      setCategories(data);
    } catch (err) {
      showToast(err.message, "error");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDelete = async (id, catName) => {
    if (window.confirm(`Delete category "${catName}"?`)) {
      try {
        const response = await fetch(`/api/categories/${id}`, {
          method: "DELETE",
        });
        if (!response.ok) throw new Error("Failed to delete.");
        showToast(`Category "${catName}" deleted.`, "danger");
        // Reload to refresh list
        setTimeout(() => window.location.reload(), 1000);
      } catch (err) {
        showToast(err.message, "error");
      }
    }
  };

  // --- NEW: Populate form for editing ---
  const handleEditClick = (cat) => {
    setEditingId(cat._id);
    setName(cat.name);
    setType(cat.type);
    setMenuSection(cat.menuSection || "Drink"); // Default if missing
    setExistingImageUrl(cat.image || "");
    setImageFile(null); // Reset file input
    window.scrollTo({ top: 0, behavior: "smooth" }); // Scroll to form
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setName("");
    setType("Product");
    setMenuSection("Drink");
    setExistingImageUrl("");
    setImageFile(null);
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setImageFile(e.target.files[0]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      showToast("Name required.", "error");
      return;
    }

    const formData = new FormData();
    formData.append("name", name);
    formData.append("type", type);

    if (type === "Menu") {
      formData.append("menuSection", menuSection);
    }

    if (imageFile) {
      formData.append("image", imageFile);
    }

    // Decide URL and Method based on Edit Mode
    const url = editingId ? `/api/categories/${editingId}` : "/api/categories";
    const method = editingId ? "PUT" : "POST";

    try {
      const response = await fetch(url, {
        method: method,
        body: formData,
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.message || "Operation failed.");

      showToast(
        editingId ? "Category updated!" : "Category created!",
        "success"
      );

      // --- REFRESH PAGE AS REQUESTED ---
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  const renderCategoryTable = (catType) => {
    const filteredCategories = categories.filter((cat) => cat.type === catType);
    return (
      <div className={styles.adminTableContainer}>
        <table className={styles.adminTable}>
          <thead>
            <tr>
              <th>Img</th>
              <th>Name</th>
              {catType === "Menu" && <th>Section</th>}
              <th style={{ textAlign: "right" }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCategories.length === 0 ? (
              <tr>
                <td colSpan="4" style={{ textAlign: "center" }}>
                  No categories found.
                </td>
              </tr>
            ) : (
              filteredCategories.map((cat) => (
                <tr
                  key={cat._id}
                  style={
                    editingId === cat._id ? { backgroundColor: "#222" } : {}
                  }
                >
                  <td>
                    {cat.image ? (
                      <img
                        src={`${cat.image}`}
                        alt="cat"
                        style={{
                          width: 40,
                          height: 40,
                          borderRadius: 4,
                          objectFit: "cover",
                        }}
                      />
                    ) : (
                      <span style={{ color: "#555" }}>No Img</span>
                    )}
                  </td>
                  <td>{cat.name}</td>
                  {catType === "Menu" && (
                    <td>
                      <span
                        style={{
                          backgroundColor:
                            cat.menuSection === "Drink" ? "#222" : "#333",
                          padding: "4px 8px",
                          borderRadius: "4px",
                          fontSize: "0.8rem",
                          color: "#ccc",
                        }}
                      >
                        {cat.menuSection || "Food"}
                      </span>
                    </td>
                  )}
                  <td className={styles.tableActions}>
                    {/* --- EDIT BUTTON --- */}
                    <button
                      className={`${styles.adminBtn} ${styles.iconBtn}`}
                      style={{ backgroundColor: "#2196F3", marginRight: "8px" }}
                      onClick={() => handleEditClick(cat)}
                      title="Edit"
                    >
                      <FaEdit />
                    </button>
                    {/* --- DELETE BUTTON --- */}
                    <button
                      className={`${styles.adminBtn} ${styles.iconBtn} ${styles.danger}`}
                      onClick={() => handleDelete(cat._id, cat.name)}
                      title="Delete"
                    >
                      <FaTrash />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className={styles.adminPage}>
      <Navbar />
      {notification && (
        <Notification
          message={notification.message}
          type={notification.type}
          onClose={() => setNotification(null)}
        />
      )}

      <div className={styles.adminHero}>
        <h1>Manage Categories</h1>
      </div>

      <div className={styles.adminContentContainer}>
        <form
          className={styles.adminEditForm}
          onSubmit={handleSubmit}
          style={editingId ? { border: "2px solid #2196F3" } : {}}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "20px",
            }}
          >
            <h2>{editingId ? "Edit Category" : "Add New Category"}</h2>
            {editingId && (
              <button
                type="button"
                onClick={handleCancelEdit}
                className={styles.adminBtn}
                style={{ backgroundColor: "#555", padding: "5px 15px" }}
              >
                <FaTimes /> Cancel Edit
              </button>
            )}
          </div>

          <div className={styles.formRowGrid}>
            <div className={styles.formGroup}>
              <label>Name</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Smoothies"
                required
              />
            </div>

            <div className={styles.formGroup}>
              <label>Type</label>
              <select value={type} onChange={(e) => setType(e.target.value)}>
                <option value="Product">Product</option>
                <option value="Menu">Menu</option>
              </select>
            </div>
          </div>

          {type === "Menu" && (
            <div
              className={styles.formGroup}
              style={{
                border: "1px solid #333",
                padding: "15px",
                borderRadius: "4px",
                backgroundColor: "#111",
              }}
            >
              <label style={{ color: "#00e5ff" }}>
                Menu Section (Required)
              </label>
              <select
                value={menuSection}
                onChange={(e) => setMenuSection(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px",
                  marginTop: "5px",
                  backgroundColor: "#000",
                  color: "#fff",
                  border: "1px solid #444",
                }}
              >
                <option value="Drink">Drink (Goes to Drinks Tab)</option>
                <option value="Food">Food (Goes to Food Tab)</option>
              </select>
            </div>
          )}

          <div className={styles.formGroup} style={{ marginTop: "20px" }}>
            <label>Background Image (Optional)</label>
            <input type="file" onChange={handleFileChange} accept="image/*" />

            {/* Show existing image preview if editing */}
            {editingId && existingImageUrl && (
              <div
                style={{
                  marginTop: "10px",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                }}
              >
                <span style={{ color: "#777", fontSize: "0.9rem" }}>
                  Current:
                </span>
                <img
                  src={`${existingImageUrl}`}
                  alt="Current"
                  style={{ height: "50px", borderRadius: "4px" }}
                />
              </div>
            )}
          </div>

          <div className={styles.formActions}>
            <button
              type="submit"
              className={`${styles.adminBtn} ${styles.primary}`}
            >
              {editingId ? (
                <>
                  <FaEdit /> Update Category
                </>
              ) : (
                <>
                  <FaPlus /> Add Category
                </>
              )}
            </button>
          </div>
        </form>

        <h2>Menu Categories</h2>
        {isLoading ? <p>Loading...</p> : renderCategoryTable("Menu")}

        <h2 style={{ marginTop: "40px" }}>Product Categories</h2>
        {isLoading ? <p>Loading...</p> : renderCategoryTable("Product")}

        <Link to="/admin/dashboard" className={styles.adminBackLink}>
          Back to Dashboard
        </Link>
      </div>
      <Footer />
    </div>
  );
}

export default AdminCategoriesPage;
